import {StyleSheet, css} from 'aphrodite';
import {Spec} from 'immutability-helper';
import React, {useState} from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {view as viewContextMenu} from 'components/ContextMenu';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {
  ActionOrigin,
  ANALYTICS_MODULE,
  COMMON_ANALYTICS_ACTIONS,
  CSV_TYPE,
} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {downloadPlateRecordCSV} from 'notebook/integrations/plate-maps/plate-record/NoteLinkedPlateRecordDAO';
import {
  getAllPlateRecordNames,
  NoteLinkedPlateRecord,
  PlateRecordNotePart,
} from 'notebook/integrations/plate-maps/plate-record/NoteLinkedPlateRecordUtil';
import PlateMapContainer from 'notebook/integrations/plate-maps/PlateMapContainer';
import ParametersForPlateMapWrapper from 'notebook/integrations/procedures/ParametersForPlateMapWrapper';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import RenameNoteLinkedObjectModal from 'notebook/notes/core-ui/RenameNoteLinkedObjectModal';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import PlateRecordSummary from 'samples/plate-maps/plate-record/PlateRecordSummary';
import useOnMount from 'ui-library/hooks/useOnMount';
import ModalAnchor from 'ui-library/ModalAnchor';
import APIUtil from 'util/APIUtil';
import {isServerRequestError} from 'util/Net';
import {ElementMouseEvent} from 'util/Types';

interface ItemProps {
  openVersionDbId: number | undefined;
}

interface NoteLinkedPlateRecordComponentProps {
  editor: NoteEditor;
  notePart: PlateRecordNotePart;
  editorProps: NoteEditorProps;
  index: number;
  itemProps: ItemProps;
  searchResultProps: MediocreSearchResultProps;
}

function NoteLinkedPlateRecordComponent({
  editor,
  notePart,
  editorProps,
  index,
  itemProps,
  searchResultProps,
}: NoteLinkedPlateRecordComponentProps): JSX.Element {
  const [renameModalOpen, setRenameModalOpen] = useState(false);

  const openContextMenu = (e: ElementMouseEvent): void => {
    e.preventDefault();
    viewContextMenu.open(
      e,
      [
        {
          name: 'Rename plate record',
          click: () => {
            setRenameModalOpen(true);
          },
        },
      ],
      true
    );
  };

  const analyticsMetadata = {
    actionOrigin: editor.isDocument() ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK,
    plateId: notePart.item.plateRecord.plateApiId,
    transferRecordId: notePart.item.plateRecord.id,
    fileId: editorProps.fileId,
  };

  useOnMount(() => {
    analytics.trackEvent(STORABLE_ANALYTICS_ACTIONS.VIEWED_PLATE_RECORD, analyticsMetadata);
  });
  const headerControls = !editorProps.viewer ? (
    <div className={css(styles.header)}>
      <SimpleDownloadAsCSVButton
        onDownloadAsCSV={async () => {
          analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.DOWNLOADED_CSV, ANALYTICS_MODULE.SAMPLES, {
            ...analyticsMetadata,
            csvType: CSV_TYPE.PLATE_RECORD,
          });
          const notificationId = _.uniqueId('note-linked-plate-record-');
          notifications.info('Downloading...', {id: notificationId, duration: 0});
          const uuid = editorProps.getIdFromCid(notePart.item.cid);
          // Since PlateRecords are created on the backend, there shouldn't ever be a case where we render
          // a plate record that doesn't have a uuid in the store.
          assert(typeof uuid === 'string', 'Expected plate record to have a uuid.', {
            cid: notePart.item.cid,
          });
          if (itemProps.openVersionDbId == null) {
            throw new Error('Expected a version id.');
          }
          try {
            const res = await downloadPlateRecordCSV({
              entryId: editorProps.fileId,
              versionDbId: itemProps.openVersionDbId,
              uuid,
            });
            notifications.success('Download completed.', {id: notificationId});
            window.open(res!.url);
          } catch (e) {
            if (isServerRequestError(e)) {
              notifications.notifyError(e, {id: notificationId});
            } else {
              throw e;
            }
          }
        }}
      />
    </div>
  ) : null;

  return (
    <SelectableNotePart indentation={notePart.indentation}>
      <PlateMapContainer
        clientStateId={`plate-record-${notePart.item.plateRecord.id}`}
        header="Annotate and fill plate"
        headerControls={headerControls}
        onHeaderContextMenu={openContextMenu}
        subheader={notePart.item.name}
        plateMapType={PlateMapType.RECORD}
      >
        <ParametersForPlateMapWrapper>
          {({isInProcedure, plateMaterialInputs, procedureStudyDesignType}) => (
            <PlateRecordSummary
              plateRecord={notePart.item.plateRecord}
              plateMaterialInputs={plateMaterialInputs}
              procedureStudyDesignType={procedureStudyDesignType}
              analyticsMetadata={analyticsMetadata}
            />
          )}
        </ParametersForPlateMapWrapper>
      </PlateMapContainer>
      <ModalAnchor open={renameModalOpen}>
        <RenameNoteLinkedObjectModal
          initialName={notePart.item.name}
          setNameAndClose={(name: string | null) => {
            if (name != null) {
              editor.storeStateForNoteLinkedObject(notePart);
              editor.updateNoteLinkedObject(index, {name});
            }
            setRenameModalOpen(false);
          }}
          isNameUsed={(name: string) => getAllPlateRecordNames(editor.props.note).includes(name)}
          maxLength={NoteConstants.MAX_NAME_LENGTH}
          title="Rename plate record"
        />
      </ModalAnchor>
    </SelectableNotePart>
  );
}

const NOTE_LINKED_PLATE_RECORD_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.NOTE_LINKED_PLATE_RECORD,
  title: 'Plate Record',
  icon: 'tube-arrow-right',
  linkable: false,
  alignable: false,
  canPasteFromOtherTenant: false,
  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: [],
    getItemProps(this: NoteEditor, item: PlateRecordNotePart, index: number): ItemProps {
      return {
        openVersionDbId: this._getVersionId(this.props.fileId, APIUtil.apiIdToType(this.props.fileId)),
      };
    },
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: PlateRecordNotePart,
      index: number,
      editorProps: NoteEditorProps,
      itemProps: ItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      return (
        <NoteLinkedPlateRecordComponent
          editor={this}
          notePart={item}
          index={index}
          editorProps={editorProps}
          itemProps={itemProps}
          searchResultProps={searchResultProps}
        />
      );
    },
    getVirtualizedHeight: (item: PlateRecordNotePart, isCollapsed: boolean) => 660,
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToText(this: NoteEditor, item: PlateRecordNotePart): string {
    // TODO(BNCH-104149): This should also be the text summary of the transfer.
    return item.item.name;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToHTML(this: NoteEditor, $el: JQuery, item: PlateRecordNotePart): JQuery {
    return $el;
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(this: NoteEditor, $el: JQuery, item: PlateRecordNotePart): null {
    notifications.warn('Cannot paste plate records.');
    return null;
  },
  getContent(this: NoteEditor, {item}: PlateRecordNotePart): {item: NoteLinkedPlateRecord} {
    return {item};
  },
  setContent(this: NoteEditor, data: unknown): Spec<NotePart> {
    throw new Error('Cannot directly create a note linked plate record.');
  },

  getReasonForDisallowedAction(
    this: NoteEditor,
    item: PlateRecordNotePart,
    action: MediocreItemAction
  ): string | null {
    return null;
  },
};

const styles = StyleSheet.create({
  header: {
    display: 'flex',
    gap: '4px',
  },
});

export default NOTE_LINKED_PLATE_RECORD_PLUGIN;
